import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Calendar.css';
import moment from 'moment';

// Helper function to determine if a booking is early or late shift
const getShiftType = (event) => {
  const startTime = event.start_time || event.startTime || '';
  
  if (!startTime) return 'early'; // Default to early if no time specified
  
  try {
    // Parse the time string (expecting format like "09:00", "12:30", etc.)
    const timeParts = startTime.split(':');
    if (timeParts.length >= 2) {
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      
      // Convert to total minutes for easier comparison
      const totalMinutes = hours * 60 + minutes;
      const noonInMinutes = 12 * 60; // 12:00 = 720 minutes
      
      return totalMinutes >= noonInMinutes ? 'late' : 'early';
    }
  } catch (error) {
    console.error('Error parsing start time:', startTime, error);
  }
  
  return 'early'; // Default to early if parsing fails
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed': return '#15a326';
    case 'new order': return '#ffffff';
    case 'requested': return '#FF8806';
    case 'talentaccepted': return '#3de651';
    case 'declined request': return '#ff3c00';
    case 'optioned': return '#0060ff';
    case 'optionSolved': return '#FFFF00';
    case 'talentOnHold': return '#FFFF00';
    case 'lano': return '#116c11';
    case 'hold': return '#800080';
    case 'confirmedCreditNote': return '#0f8f8f';
    case 'Booked': return '#ADD8E6';
    default: return '#606060';
  }
};

// Tooltip styles
const tooltipStyles = {
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
    color: '#ffffff',
    padding: '10px',
    gap: '15px'
  },
  field: {
    marginBottom: '12px'
  },
  label: {
    fontWeight: '500',
    fontSize: '.9rem',
    marginBottom: '3px',
    color: '#ccc'
  },
  value: {
    fontSize: '1rem',
    fontWeight: '400',
    wordBreak: 'break-word'
  }
};

// Helper for getting display value from potentially nested objects
const getDisplayValue = (value) => {
  if (!value) return 'N/A';
  
  if (typeof value === 'object') {
    // Handle different object structures
    if (value.label) return value.label;
    if (value.name) return value.name;
    if (value.value) return value.value;
    if (value.short) return value.short;
    if (value.text) return value.text;
    
    // If it's a date object
    if (value instanceof Date) return moment(value).format('MMM D, YYYY');
    
    // Try to convert to string as fallback
    return JSON.stringify(value);
  }
  
  return value;
};

// Helper to get location display
const getLocationDisplay = (event) => {
  if (!event) return 'N/A';
  
  if (event.location) {
    if (typeof event.location === 'object') {
      return event.location.name || event.location.short || JSON.stringify(event.location);
    }
    return event.location;
  }
  
  if (event.location_name) return event.location_name;
  if (event.location_city) return event.location_city;
  
  return 'N/A';
};

// Helper to get studio display
const getStudioDisplay = (event) => {
  if (!event) return 'N/A';
  
  if (event.studio) {
    if (typeof event.studio === 'object') {
      return event.studio.name || JSON.stringify(event.studio);
    }
    return event.studio;
  }
  
  return 'N/A';
};

// Month Event component - Similar to AllBookings.MonthEvent
export const MonthEvent = ({ event, onEventClick }) => {
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (onEventClick) {
      onEventClick(event);
    } else {
      navigate(`/admin/detail?id=${event.id}`);
    }
  };
  
  const borderRadius = event.set === 'On Model' ? '8px' : '0px';
  const shiftType = getShiftType(event);
  const shiftClass = shiftType === 'late' ? 'late-shifts' : 'early-shifts';

  const groupBadge = event.groupColor ? (
    <div style={{
      display: 'inline-block',
      backgroundColor: event.groupColor,
      color: '#ffffff',
      padding: '3px 6px',
      borderRadius: '10px',
      fontSize: '0.8em',
      margin: '0 4px',
      verticalAlign: 'middle'
    }}>        
      {event.group}
    </div>
  ) : null;
  
  return (
    <div 
      className={`custom-event month-event ${shiftClass}`} 
      onClick={handleClick}
      style={{ 
        borderRadius: borderRadius
      }}
    >
      <div 
        className="status-indicator small" 
        style={{ backgroundColor: getStatusColor(event.status) }}
      ></div>
      
      <div className="month-event-content">
                    <div className="event-title small">{getDisplayValue(event.client)}</div>
            <div className="event-detail small">{getDisplayValue(event.talent)}</div>
      </div>
      
      <div className="month-event-actions">
        {event.group && event.groupColor && (
          <div 
            className="event-group-badge small" 
            style={{ backgroundColor: event.groupColor }}
          >
            {event.group}
          </div>
        )}
        
        <a 
          href={`/admin/detail?id=${event.id}`}
          className="month-edit-link"
          onClick={(e) => e.stopPropagation()}
        >
          Edit
        </a>
      </div>
    </div>
  );
};

// Week Event component - Similar to AllBookings.WeekEvent
export const WeekEvent = ({ event, onEventClick }) => {
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);
  
  const handleClick = (e) => {
    e.stopPropagation();
    
    // If modal is open, don't trigger navigation
    if (showDetail) {
      return;
    }
    
    if (onEventClick) {
      onEventClick(event);
    } else {
      navigate(`/admin/detail?id=${event.id}`);
    }
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDetail(!showDetail);
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDetail(false);
  };
  
  const borderRadius = event.set === 'On Model' ? '10px' : '0px';
  const shiftType = getShiftType(event);
  const shiftClass = shiftType === 'late' ? 'late-shifts' : 'early-shifts';
  
  // Format the date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('MMM D, YYYY');
  };
  
  return (
    <>
      <div 
        className={`custom-event week-event ${shiftClass}`} 
        onClick={handleClick}
        style={{ 
          borderRadius: borderRadius,
          margin: '5px 0',
          position: 'relative',
          cursor: 'pointer',
        }}
      > 
        <div className="status-indicator" style={{ backgroundColor: getStatusColor(event.status) }}></div>
        
        <div className="event-content">
          <div className="event-title">{getDisplayValue(event.client) || 'Unknown Client'}</div>
          <div className="event-title">{getDisplayValue(event.talent_name || event.talent) || 'Unknown Talent'}</div>
          <div className="event-detail">{getDisplayValue(event.position) || 'N/A'}</div>
        </div>
        
        <div className="event-actions">
          {event.group && event.groupColor && (
            <div className="event-group-badge" style={{ backgroundColor: event.groupColor }}>
              {event.group}
            </div>
          )}
          
          <div className="event-buttons">
            <button 
              className="event-button detail-button"
              onClick={handleMoreClick}
            >
              More
            </button>
            
            <a 
              href={`/admin/detail?id=${event.id}`}
              className="event-button edit-button"
              onClick={(e) => e.stopPropagation()}
            >
              Edit
            </a>
          </div>
        </div>
      </div>
      
      {showDetail && (
        <div 
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleCloseModal}
        >
          <div 
            className="event-detail-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              transform: 'none',
              top: 'auto',
              left: 'auto',
              zIndex: 99999
            }}
          >
            <div className="event-detail-header">
              <h3>{getDisplayValue(event.client) || 'Unknown Client'} - {getDisplayValue(event.talent_name || event.talent) || 'Unknown Talent'}</h3>
              <span 
                className="close-button"
                onClick={handleCloseModal}
              >
                &times;
              </span>
            </div>
            
            <div className="event-detail-content" style={tooltipStyles.container}>
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Client:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.client)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Talent:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.talent_name || event.talent)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Position:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.position)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Status:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.status) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Start:</div>
                <div style={tooltipStyles.value}>{formatDate(event.start_date)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>End:</div>
                <div style={tooltipStyles.value}>{formatDate(event.end_date)}</div>
              </div>

              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Location:</div>
                <div style={tooltipStyles.value}>{getLocationDisplay(event)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Studio:</div>
                <div style={tooltipStyles.value}>{getStudioDisplay(event)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Option:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.option)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Set:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.set) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>KPI:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.kpi) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Additional Info:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.SonstigeInfo || event.sonstige_info)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Created By:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.created_by) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Processing By:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.processing_by) || getDisplayValue(event.proposed_by) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Created At:</div>
                <div style={tooltipStyles.value}>
                  {event.created_at ? moment(event.created_at).format('DD-MM-YYYY HH:mm') : 'N/A'}
                </div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Updated At:</div>
                <div style={tooltipStyles.value}>
                  {event.updated_at ? moment(event.updated_at).format('DD-MM-YYYY HH:mm') : 'N/A'}
                </div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Status Updated At:</div>
                <div style={tooltipStyles.value}>
                  {event.status_updated_at ? moment(event.status_updated_at).format('DD-MM-YYYY HH:mm') : 'N/A'}
                </div>
              </div>
              
              {event.group && (
                <div style={tooltipStyles.field}>
                  <div style={tooltipStyles.label}>Group:</div>
                  <div style={tooltipStyles.value}>
                    <span 
                      className="group-badge"
                      style={{ backgroundColor: event.group_color || '#888', padding: '2px 6px', borderRadius: '4px' }}
                    >
                      {event.group}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="event-detail-footer">
              <a 
                href={`/admin/detail?id=${event.id}`}
                className="edit-link"
                onClick={(e) => e.stopPropagation()}
              >
                Edit Booking
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Day/Agenda Event component - Similar to AllBookings.AgendaEvent
export const DayEvent = ({ event, onEventClick }) => {
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);
  
  const handleClick = (e) => {
    e.stopPropagation();
    
    // If modal is open, don't trigger navigation
    if (showDetail) {
      return;
    }
    
    if (onEventClick) {
      onEventClick(event);
    } else {
      navigate(`/admin/detail?id=${event.id}`);
    }
  };

  const handleMoreClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDetail(!showDetail);
  };

  const handleCloseModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowDetail(false);
  };
  
  const shiftType = getShiftType(event);
  const shiftClass = shiftType === 'late' ? 'late-shifts' : 'early-shifts';
  
  return (
    <>
      <div 
        className={`custom-event day-event ${shiftClass}`} 
        onClick={handleClick}
        style={{ 
          borderRadius: event.set === 'On Model' ? '10px' : '0px'
        }}
      >
        <div className="status-indicator" style={{ backgroundColor: getStatusColor(event.status) }}></div>
        
        <div className="event-content">
          <div className="event-title">{getDisplayValue(event.client)}</div>
          <div className="event-detail">{getDisplayValue(event.talent)}</div>
          <div className="event-detail">{getDisplayValue(event.startTime) || event.start_time || 'N/A'} - {getDisplayValue(event.endTime) || event.end_time || 'N/A'}</div>
          <div className="event-detail">{getDisplayValue(event.proposedBy) || 'N/A'}</div>
        </div>
        
        <div className="event-actions">
          {event.group && event.groupColor && (
            <div className="event-group-badge" style={{ backgroundColor: event.groupColor }}>
              {event.group}
            </div>
          )}
          
          <div className="event-buttons">
            <button 
              className="event-button detail-button"
              onClick={handleMoreClick}
            >
              More
            </button>
            
            <a 
              href={`/admin/detail?id=${event.id}`}
              className="event-button edit-button"
              onClick={(e) => e.stopPropagation()}
            >
              Edit
            </a>
          </div>
        </div>
      </div>
      
      {showDetail && (
        <div 
          className="modal-backdrop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99998,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleCloseModal}
        >
          <div 
            className="event-detail-popup"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              transform: 'none',
              top: 'auto',
              left: 'auto',
              zIndex: 99999
            }}
          >
            <div className="event-detail-header">
              <h3>{getDisplayValue(event.client)} - {getDisplayValue(event.talent)}</h3>
              <span 
                className="close-button"
                onClick={handleCloseModal}
              >
                &times;
              </span>
            </div>
            
            <div className="event-detail-content" style={tooltipStyles.container}>
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Client:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.client)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Talent:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.talent)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Status:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.status) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Proposed By:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.proposedBy) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Set:</div>
                <div style={tooltipStyles.value}>{getDisplayValue(event.set) || 'N/A'}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Location:</div>
                <div style={tooltipStyles.value}>{getLocationDisplay(event)}</div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Time:</div>
                <div style={tooltipStyles.value}>
                  {event.startTime || 'N/A'} - {event.endTime || 'N/A'}
                </div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Created At:</div>
                <div style={tooltipStyles.value}>
                  {event.created_at ? moment(event.created_at).format('DD-MM-YYYY HH:mm') : 'N/A'}
                </div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Updated At:</div>
                <div style={tooltipStyles.value}>
                  {event.updated_at ? moment(event.updated_at).format('DD-MM-YYYY HH:mm') : 'N/A'}
                </div>
              </div>
              
              <div style={tooltipStyles.field}>
                <div style={tooltipStyles.label}>Status Updated At:</div>
                <div style={tooltipStyles.value}>
                  {event.status_updated_at ? moment(event.status_updated_at).format('DD-MM-YYYY HH:mm') : 'N/A'}
                </div>
              </div>
              
              {event.group && (
                <div style={tooltipStyles.field}>
                  <div style={tooltipStyles.label}>Group:</div>
                  <div style={tooltipStyles.value}>
                    <span 
                      className="group-badge"
                      style={{ backgroundColor: event.groupColor || '#888', padding: '2px 6px', borderRadius: '4px' }}
                    >
                      {event.group}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="event-detail-footer">
              <a 
                href={`/admin/detail?id=${event.id}`}
                className="edit-link"
                onClick={(e) => e.stopPropagation()}
              >
                Edit Booking
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default { MonthEvent, WeekEvent, DayEvent }; 